import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert, Box, Button, Grid, IconButton, Snackbar, Stack,
    TextField, Tooltip, Typography
} from "@mui/material";
import {
    Search as IconSearch,
    RestartAlt as IconRestart,
    ContentCopy as IconContentCopy,
    Clear as IconClear
} from "@mui/icons-material";
import moment from "moment";

import * as api from "../../services/api";
import DataGridBase from "../../components/common/DataGridBase";

const FILTROS_VACIOS = {
    fechaInicio: "",
    fechaFin: "",
    usuario: "",
    requestId: "",
};

const obtenerFiltrosUltimos5Minutos = () => ({
    fechaInicio: moment().subtract(5, "minutes").format("YYYY-MM-DDTHH:mm"),
    fechaFin: "",
    usuario: "",
    requestId: "",
});

// Evalua si una fila de cualquier bitacora contiene el texto buscado en cualquiera de sus datos.
const cumpleFiltroGlobal = (row, term) => {
    if (!term || !term.trim()) return true;
    const t = term.trim().toLowerCase();
    return Object.keys(row).some((k) => {
        if (k === "id") return false;
        const v = row[k];
        if (v == null) return false;
        if (typeof v === "object") return false;

        // Compara tanto la fecha original como su representacion formateada
        if (k.toLowerCase().includes("fecha") && moment.utc(v).isValid()) {
            const fmt = moment.utc(v).format("DD/MM/YYYY HH:mm:ss");
            if (fmt.toLowerCase().includes(t)) return true;
        }

        return String(v).toLowerCase().includes(t);
    });
};

const Monitoreo = () => {
    const [cargando, setCargando] = useState(true);
    const [cambios, setCambios] = useState([]);
    const [logins, setLogins] = useState([]);
    const [errores, setErrores] = useState([]);
    const [filtros, setFiltros] = useState(obtenerFiltrosUltimos5Minutos);

    // Filtros de texto por bitacora (al lado izquierdo de la paginacion)
    const [busquedaCambio, setBusquedaCambio] = useState("");
    const [filtroAplicadoCambio, setFiltroAplicadoCambio] = useState("");

    const [busquedaLogin, setBusquedaLogin] = useState("");
    const [filtroAplicadoLogin, setFiltroAplicadoLogin] = useState("");

    const [busquedaError, setBusquedaError] = useState("");
    const [filtroAplicadoError, setFiltroAplicadoError] = useState("");

    const [mensajeSnackbar, setMensajeSnackbar] = useState({ open: false, severity: "success", texto: "" });
    const cerrarSnackbar = () => setMensajeSnackbar((prev) => ({ ...prev, open: false }));

    const fetchAll = useCallback(async (f) => {
        setCargando(true);
        const base = { ...filtros, ...f };
        const mkParams = (extra) => {
            const params = new URLSearchParams();
            const add = (k, v) => { if (v !== null && v !== undefined && v !== "" && v !== false) params.append(k, v); };
            if (base.fechaInicio) params.append("fechaInicioUtc", new Date(base.fechaInicio).toISOString());
            if (base.fechaFin) params.append("fechaFinUtc", new Date(base.fechaFin).toISOString());
            if (base.usuario) params.append("usuario", base.usuario);
            if (base.requestId) params.append("requestId", base.requestId);
            Object.entries(extra).forEach(([k, v]) => add(k, v));
            return params;
        };

        try {
            const [r1, r2, r3] = await Promise.all([
                api.default.get("/BitacoraDashboard/Gets?" + mkParams({ incluirCambios: true, incluirLogin: false, incluirErrores: false }).toString()),
                api.default.get("/BitacoraDashboard/Gets?" + mkParams({ incluirCambios: false, incluirLogin: true, incluirErrores: false }).toString()),
                api.default.get("/BitacoraDashboard/Gets?" + mkParams({ incluirCambios: false, incluirLogin: false, incluirErrores: true }).toString()),
            ]);
            setCambios((r1.data || []).map((r, i) => ({ ...r, id: "c" + i })));
            setLogins((r2.data || []).map((r, i) => ({ ...r, id: "l" + i })));
            setErrores((r3.data || []).map((r, i) => ({ ...r, id: "e" + i })));
        } catch (err) {
            console.log("Err: ", err);
        }
        setCargando(false);
    }, [filtros]);

    useEffect(() => {
        const ini = obtenerFiltrosUltimos5Minutos();
        setFiltros(ini);
        fetchAll(ini);
    }, []);

    const cambiar = (nombre, valor) => setFiltros((a) => ({ ...a, [nombre]: valor }));
    const limpiar = () => {
        setFiltros(FILTROS_VACIOS);
        setBusquedaCambio("");
        setFiltroAplicadoCambio("");
        setBusquedaLogin("");
        setFiltroAplicadoLogin("");
        setBusquedaError("");
        setFiltroAplicadoError("");
        fetchAll(FILTROS_VACIOS);
    };

    const limpiarCambio = () => {
        setBusquedaCambio("");
        setFiltroAplicadoCambio("");
    };

    const limpiarLogin = () => {
        setBusquedaLogin("");
        setFiltroAplicadoLogin("");
    };

    const limpiarError = () => {
        setBusquedaError("");
        setFiltroAplicadoError("");
    };

    // Filas filtradas por el buscador global de cada tabla
    const cambiosFiltrados = useMemo(
        () => cambios.filter((r) => cumpleFiltroGlobal(r, filtroAplicadoCambio)),
        [cambios, filtroAplicadoCambio]
    );

    const loginsFiltrados = useMemo(
        () => logins.filter((r) => cumpleFiltroGlobal(r, filtroAplicadoLogin)),
        [logins, filtroAplicadoLogin]
    );

    const erroresFiltrados = useMemo(
        () => errores.filter((r) => cumpleFiltroGlobal(r, filtroAplicadoError)),
        [errores, filtroAplicadoError]
    );

    // Opcion para copiar todo el resultado de las 3 tablas en formato estructurado
    const copiarResultados = async () => {
        const lineas = [];
        const ahora = moment().format("DD/MM/YYYY HH:mm:ss");
        lineas.push("================================================================================");
        lineas.push("DASHBOARD DE MONITOREO - REPORTE DE BITÁCORAS");
        lineas.push(`Fecha de Copia: ${ahora}`);
        lineas.push(`Filtros: Desde=${filtros.fechaInicio || "(Auto)"} | Hasta=${filtros.fechaFin || "(Auto)"} | Usuario=${filtros.usuario || "(Todos)"} | RequestId=${filtros.requestId || "(Todos)"}`);
        lineas.push("================================================================================\n");

        // 1. CAMBIOS
        lineas.push(`--- 1. BITÁCORA DE CAMBIOS (${cambiosFiltrados.length} registros) ---`);
        if (cambiosFiltrados.length === 0) {
            lineas.push("(Sin registros)");
        } else {
            cambiosFiltrados.forEach((r, idx) => {
                const f = r.fechaHoraUtc && moment.utc(r.fechaHoraUtc).isValid() ? moment.utc(r.fechaHoraUtc).format("DD/MM/YYYY HH:mm:ss") : "";
                lineas.push(`${idx + 1}. [${f}] Usu:${r.usuario || "-"} | Tabla:${r.tabla || "-"} | Campo:${r.campo || "-"} | Llaves:${r.llaves || "-"} | Op:${r.operacion || "-"} | Ant:${r.valorAnterior || ""} -> Post:${r.valorPosterior || ""} | ReqId:${r.requestId || "-"}`);
            });
        }
        lineas.push("\n");

        // 2. LOGIN
        lineas.push(`--- 2. BITÁCORA DE LOGIN (${loginsFiltrados.length} registros) ---`);
        if (loginsFiltrados.length === 0) {
            lineas.push("(Sin registros)");
        } else {
            loginsFiltrados.forEach((r, idx) => {
                const f = r.fechaHoraUtc && moment.utc(r.fechaHoraUtc).isValid() ? moment.utc(r.fechaHoraUtc).format("DD/MM/YYYY HH:mm:ss") : "";
                lineas.push(`${idx + 1}. [${f}] Usu:${r.usuario || "-"} | Res:${r.resultado || "-"} | Motivo:${r.motivo || "-"} | IP:${r.ipAddress || "-"} | Dev:${r.tipoDispositivo || "-"} | SO:${r.sistemaOperativo || "-"} | Nav:${r.navegador || "-"} (${r.versionNavegador || ""}) | Ms:${r.tiempoRespuestaMs ?? "-"} | ReqId:${r.requestId || "-"}`);
            });
        }
        lineas.push("\n");

        // 3. ERRORES
        lineas.push(`--- 3. BITÁCORA DE ERRORES (${erroresFiltrados.length} registros) ---`);
        if (erroresFiltrados.length === 0) {
            lineas.push("(Sin registros)");
        } else {
            erroresFiltrados.forEach((r, idx) => {
                const f = r.fechaHoraUtc && moment.utc(r.fechaHoraUtc).isValid() ? moment.utc(r.fechaHoraUtc).format("DD/MM/YYYY HH:mm:ss") : "";
                lineas.push(`${idx + 1}. [${f}] [${r.nivel || "ERROR"}] [${r.categoria || "-"}] Cod:${r.codigoError || "-"} | HTTP:${r.codigoHttp ?? "-"} | Endpoint:${r.endpoint || "-"} | Usu:${r.usuario || "-"} | Msg:${r.mensaje || "-"} | ReqId:${r.requestId || "-"}${r.detalleTecnico ? " | Det:" + r.detalleTecnico : ""}`);
            });
        }

        const textoCompleto = lineas.join("\n");
        try {
            await navigator.clipboard.writeText(textoCompleto);
            setMensajeSnackbar({ open: true, severity: "success", texto: "Se copiaron los resultados de las 3 tablas formateados al portapapeles" });
        } catch (e) {
            setMensajeSnackbar({ open: true, severity: "error", texto: "No se pudo copiar al portapapeles" });
        }
    };

    const colsCambio = [
        { field: 'fechaHoraUtc', headerName: 'Fecha UTC', flex: 0, width: 130, minWidth: 130, sortable: false,
            valueFormatter: (v) => (v && moment.utc(v).isValid() ? moment.utc(v).format("DD/MM HH:mm:ss") : "") },
        { field: 'usuario', headerName: 'Usuario', flex: 0, width: 70, minWidth: 70, sortable: false },
        { field: 'tabla', headerName: 'Tabla', flex: 0.6, minWidth: 80, sortable: false },
        { field: 'campo', headerName: 'Campo', flex: 0.6, minWidth: 80, sortable: false },
        { field: 'llaves', headerName: 'Llaves', flex: 0.8, minWidth: 80, sortable: false },
        { field: 'operacion', headerName: 'Operación', flex: 0.5, minWidth: 75, sortable: false },
        { field: 'valorAnterior', headerName: 'Anterior', flex: 0.8, minWidth: 80, sortable: false },
        { field: 'valorPosterior', headerName: 'Posterior', flex: 0.8, minWidth: 80, sortable: false },
        { field: 'requestId', headerName: 'RequestId', flex: 0.6, minWidth: 80, sortable: false },
    ];

    const colsLogin = [
        { field: 'fechaHoraUtc', headerName: 'Fecha UTC', flex: 0, width: 130, minWidth: 130, sortable: false,
            valueFormatter: (v) => (v && moment.utc(v).isValid() ? moment.utc(v).format("DD/MM HH:mm:ss") : "") },
        { field: 'usuario', headerName: 'Usuario', flex: 0, width: 70, minWidth: 70, sortable: false },
        { field: 'resultado', headerName: 'Resultado', flex: 0.6, minWidth: 80, sortable: false },
        { field: 'motivo', headerName: 'Motivo', flex: 0.8, minWidth: 100, sortable: false },
        { field: 'ipAddress', headerName: 'IP', flex: 0.6, minWidth: 90, sortable: false },
        { field: 'navegador', headerName: 'Navegador', flex: 0.6, minWidth: 80, sortable: false },
        { field: 'sistemaOperativo', headerName: 'SO', flex: 0.5, minWidth: 70, sortable: false },
        { field: 'tipoDispositivo', headerName: 'Dispositivo', flex: 0.6, minWidth: 80, sortable: false },
        { field: 'tiempoRespuestaMs', headerName: 'Ms', flex: 0.4, minWidth: 50, sortable: false },
        { field: 'requestId', headerName: 'RequestId', flex: 0.6, minWidth: 80, sortable: false },
    ];

    const colsError = [
        { field: 'fechaHoraUtc', headerName: 'Fecha UTC', flex: 0, width: 130, minWidth: 130, sortable: false,
            valueFormatter: (v) => (v && moment.utc(v).isValid() ? moment.utc(v).format("DD/MM HH:mm:ss") : "") },
        { field: 'nivel', headerName: 'Nivel', flex: 0.5, minWidth: 60, sortable: false },
        { field: 'categoria', headerName: 'Categoria', flex: 0.6, minWidth: 80, sortable: false },
        { field: 'codigoError', headerName: 'CodigoError', flex: 0.7, minWidth: 90, sortable: false },
        { field: 'mensaje', headerName: 'Mensaje', flex: 1, minWidth: 120, sortable: false },
        { field: 'modulo', headerName: 'Modulo', flex: 0.6, minWidth: 80, sortable: false },
        { field: 'operacion', headerName: 'Operacion', flex: 0.7, minWidth: 90, sortable: false },
        { field: 'usuario', headerName: 'Usuario', flex: 0.5, minWidth: 70, sortable: false },
        { field: 'codigoHttp', headerName: 'Http', flex: 0.4, minWidth: 50, sortable: false },
        { field: 'endpoint', headerName: 'Endpoint', flex: 1, minWidth: 120, sortable: false },
        { field: 'requestId', headerName: 'RequestId', flex: 0.6, minWidth: 80, sortable: false },
    ];

    return (
        <div>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Dashboard de Monitoreo</Typography>
                </Grid>

                {/* Filtros principales */}
                <Grid container spacing={1} padding={"4px 0px 4px 0px"} alignItems="center">
                    <Grid item xs={6} sm={2.5} md={2}>
                        <TextField
                            name="fechaInicio"
                            label="Desde"
                            type="datetime-local"
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={filtros.fechaInicio}
                            onChange={(e) => cambiar("fechaInicio", e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") fetchAll(filtros); }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={2.5} md={2}>
                        <TextField
                            name="fechaFin"
                            label="Hasta"
                            type="datetime-local"
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={filtros.fechaFin}
                            onChange={(e) => cambiar("fechaFin", e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") fetchAll(filtros); }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={2} md={1.5}>
                        <TextField
                            name="usuario"
                            label="Usuario"
                            fullWidth
                            size="small"
                            value={filtros.usuario}
                            onChange={(e) => cambiar("usuario", e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") fetchAll(filtros); }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={2.5} md={2}>
                        <TextField
                            name="requestId"
                            label="RequestId"
                            fullWidth
                            size="small"
                            value={filtros.requestId}
                            onChange={(e) => cambiar("requestId", e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") fetchAll(filtros); }}
                        />
                    </Grid>
                    <Grid item xs={4} sm={1} md={0.8}>
                        <Tooltip title="Buscar">
                            <Button variant="outlined" fullWidth size="medium" onClick={() => fetchAll(filtros)}>
                                <IconSearch />
                            </Button>
                        </Tooltip>
                    </Grid>
                    <Grid item xs={4} sm={1} md={0.8}>
                        <Tooltip title="Limpiar Filtros">
                            <Button variant="outlined" fullWidth size="medium" onClick={limpiar}>
                                <IconRestart />
                            </Button>
                        </Tooltip>
                    </Grid>
                    <Grid item xs={4} sm={2.5} md={2.5}>
                        <Tooltip title="Copiar resultados de las 3 tablas en formato texto">
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                size="medium"
                                startIcon={<IconContentCopy />}
                                onClick={copiarResultados}
                                sx={{ textTransform: "none", fontWeight: "bold" }}
                            >
                                Copiar Todo
                            </Button>
                        </Tooltip>
                    </Grid>
                </Grid>

                {/* 1. Tabla: Bitacora de Cambios */}
                <Grid item xs={12} sx={{ mt: 0.5 }}>
                    <DataGridBase
                        rows={cambiosFiltrados}
                        columns={colsCambio}
                        loading={cargando}
                        showToolbar={true}
                        hideAuditToggle={true}
                        pageSize={10}
                        toolbarLeft={
                            <Typography sx={{ fontWeight: 'bold', fontSize: '0.875rem', pl: 0.5, color: 'primary.main' }}>
                                {"Bitacora de Cambios (" + cambiosFiltrados.length + (cambiosFiltrados.length !== cambios.length ? ` de ${cambios.length}` : "") + ")"}
                            </Typography>
                        }
                        toolbarRight={
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <TextField
                                    size="small"
                                    placeholder="Filtrar cambios..."
                                    value={busquedaCambio}
                                    onChange={(e) => setBusquedaCambio(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            setFiltroAplicadoCambio(busquedaCambio);
                                        } else if (e.key === "Escape") {
                                            e.preventDefault();
                                            limpiarCambio();
                                        }
                                    }}
                                    sx={{
                                        width: { xs: 130, sm: 180, md: 220 },
                                        "& .MuiInputBase-root": { height: 28, fontSize: "0.8rem" }
                                    }}
                                />
                                <Tooltip title="Filtrar">
                                    <IconButton
                                        size="small"
                                        onClick={() => setFiltroAplicadoCambio(busquedaCambio)}
                                        color="primary"
                                        sx={{ p: 0.5 }}
                                    >
                                        <IconSearch fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Limpiar filtro">
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={limpiarCambio}
                                            disabled={!busquedaCambio && !filtroAplicadoCambio}
                                            color="default"
                                            sx={{ p: 0.5 }}
                                        >
                                            <IconClear fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                        }
                    />
                </Grid>

                {/* 2. Tabla: Bitacora de Login */}
                <Grid item xs={12} sx={{ mt: 0.5 }}>
                    <DataGridBase
                        rows={loginsFiltrados}
                        columns={colsLogin}
                        loading={cargando}
                        showToolbar={true}
                        hideAuditToggle={true}
                        pageSize={10}
                        toolbarLeft={
                            <Typography sx={{ fontWeight: 'bold', fontSize: '0.875rem', pl: 0.5, color: 'primary.main' }}>
                                {"Bitacora de Login (" + loginsFiltrados.length + (loginsFiltrados.length !== logins.length ? ` de ${logins.length}` : "") + ")"}
                            </Typography>
                        }
                        toolbarRight={
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <TextField
                                    size="small"
                                    placeholder="Filtrar logins..."
                                    value={busquedaLogin}
                                    onChange={(e) => setBusquedaLogin(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            setFiltroAplicadoLogin(busquedaLogin);
                                        } else if (e.key === "Escape") {
                                            e.preventDefault();
                                            limpiarLogin();
                                        }
                                    }}
                                    sx={{
                                        width: { xs: 130, sm: 180, md: 220 },
                                        "& .MuiInputBase-root": { height: 28, fontSize: "0.8rem" }
                                    }}
                                />
                                <Tooltip title="Filtrar">
                                    <IconButton
                                        size="small"
                                        onClick={() => setFiltroAplicadoLogin(busquedaLogin)}
                                        color="primary"
                                        sx={{ p: 0.5 }}
                                    >
                                        <IconSearch fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Limpiar filtro">
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={limpiarLogin}
                                            disabled={!busquedaLogin && !filtroAplicadoLogin}
                                            color="default"
                                            sx={{ p: 0.5 }}
                                        >
                                            <IconClear fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                        }
                    />
                </Grid>

                {/* 3. Tabla: Bitacora de Errores */}
                <Grid item xs={12} sx={{ mt: 0.5 }}>
                    <DataGridBase
                        rows={erroresFiltrados}
                        columns={colsError}
                        loading={cargando}
                        showToolbar={true}
                        hideAuditToggle={true}
                        pageSize={10}
                        toolbarLeft={
                            <Typography sx={{ fontWeight: 'bold', fontSize: '0.875rem', pl: 0.5, color: 'primary.main' }}>
                                {"Bitacora de Errores (" + erroresFiltrados.length + (erroresFiltrados.length !== errores.length ? ` de ${errores.length}` : "") + ")"}
                            </Typography>
                        }
                        toolbarRight={
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <TextField
                                    size="small"
                                    placeholder="Filtrar errores..."
                                    value={busquedaError}
                                    onChange={(e) => setBusquedaError(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            setFiltroAplicadoError(busquedaError);
                                        } else if (e.key === "Escape") {
                                            e.preventDefault();
                                            limpiarError();
                                        }
                                    }}
                                    sx={{
                                        width: { xs: 130, sm: 180, md: 220 },
                                        "& .MuiInputBase-root": { height: 28, fontSize: "0.8rem" }
                                    }}
                                />
                                <Tooltip title="Filtrar">
                                    <IconButton
                                        size="small"
                                        onClick={() => setFiltroAplicadoError(busquedaError)}
                                        color="primary"
                                        sx={{ p: 0.5 }}
                                    >
                                        <IconSearch fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Limpiar filtro">
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={limpiarError}
                                            disabled={!busquedaError && !filtroAplicadoError}
                                            color="default"
                                            sx={{ p: 0.5 }}
                                        >
                                            <IconClear fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                        }
                    />
                </Grid>
            </Grid>

            <Snackbar
                open={mensajeSnackbar.open}
                autoHideDuration={4000}
                onClose={cerrarSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={cerrarSnackbar} severity={mensajeSnackbar.severity} sx={{ width: '100%' }}>
                    {mensajeSnackbar.texto}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default Monitoreo;
